import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * PartnerAuthContext — isolated from any existing /admin or /sovereign flow.
 * JWT stored in localStorage, attached via axios Authorization header.
 */
const KEY = "eigoo_partner_token";
const Ctx = createContext(null);

export function PartnerAuthProvider({ children }) {
    const [token, setToken] = useState(() => localStorage.getItem(KEY));
    const [partner, setPartner] = useState(null);
    const [status, setStatus] = useState("checking"); // checking | authed | guest

    const applyAuthHeader = useCallback((t) => {
        if (t) {
            api.defaults.headers.common.Authorization = `Bearer ${t}`;
        } else {
            delete api.defaults.headers.common.Authorization;
        }
    }, []);

    const fetchMe = useCallback(async () => {
        try {
            const { data } = await api.get("/partner/me");
            setPartner(data);
            setStatus("authed");
            return data;
        } catch {
            localStorage.removeItem(KEY);
            applyAuthHeader(null);
            setToken(null);
            setPartner(null);
            setStatus("guest");
            return null;
        }
    }, [applyAuthHeader]);

    useEffect(() => {
        if (!token) {
            setStatus("guest");
            return;
        }
        applyAuthHeader(token);
        fetchMe();
    }, [token, applyAuthHeader, fetchMe]);

    const persistToken = (t) => {
        localStorage.setItem(KEY, t);
        applyAuthHeader(t);
        setToken(t);
    };

    const signin = async (email, password) => {
        const { data } = await api.post("/partner/signin", { email, password });
        persistToken(data.token);
        setPartner({
            email: data.email,
            legal_name: data.legal_name,
            display_name: data.display_name,
            legal_name_pending: data.legal_name_pending,
            agreement_accepted: data.agreement_accepted,
            agreement_accepted_at: data.agreement_accepted_at,
            addendum_a_accepted: data.addendum_a_accepted,
            addendum_a_accepted_at: data.addendum_a_accepted_at,
        });
        setStatus("authed");
        return data;
    };

    const signup = async (email, password, extras = {}) => {
        const { data } = await api.post("/partner/signup", {
            email,
            password,
            legal_name: extras.legal_name,
            display_name: extras.display_name,
            invite_token: extras.invite_token,
        });
        persistToken(data.token);
        setPartner({
            email: data.email,
            legal_name: data.legal_name,
            display_name: data.display_name,
            legal_name_pending: data.legal_name_pending,
            agreement_accepted: data.agreement_accepted,
            agreement_accepted_at: data.agreement_accepted_at,
            addendum_a_accepted: data.addendum_a_accepted,
            addendum_a_accepted_at: data.addendum_a_accepted_at,
        });
        setStatus("authed");
        return data;
    };

    const setPartnerNames = async (legalName, displayName) => {
        const { data } = await api.post("/partner/profile/names", {
            legal_name: legalName,
            display_name: displayName,
        });
        setPartner((p) =>
            p
                ? {
                      ...p,
                      legal_name: data.legal_name,
                      display_name: data.display_name,
                      legal_name_pending: false,
                  }
                : p,
        );
        return data;
    };

    const acceptAgreement = async () => {
        const { data } = await api.post("/partner/agreement/accept", {
            nda_accepted: true,
            operator_accepted: true,
        });
        setPartner((p) =>
            p
                ? {
                      ...p,
                      agreement_accepted: true,
                      agreement_accepted_at: data.accepted_at,
                  }
                : p,
        );
        return data;
    };

    const acceptAddendumA = async () => {
        const { data } = await api.post("/partner/addendum-a/accept");
        setPartner((p) =>
            p
                ? {
                      ...p,
                      addendum_a_accepted: true,
                      addendum_a_accepted_at: data.addendum_a_accepted_at,
                      addendum_a_version: data.addendum_a_version,
                  }
                : p,
        );
        return data;
    };

    const signout = () => {
        localStorage.removeItem(KEY);
        applyAuthHeader(null);
        setToken(null);
        setPartner(null);
        setStatus("guest");
    };

    return (
        <Ctx.Provider
            value={{
                status,
                partner,
                signin,
                signup,
                signout,
                acceptAgreement,
                acceptAddendumA,
                setPartnerNames,
                refresh: fetchMe,
            }}
        >
            {children}
        </Ctx.Provider>
    );
}

export const usePartnerAuth = () => {
    const v = useContext(Ctx);
    if (!v) throw new Error("usePartnerAuth outside provider");
    return v;
};

export function formatApiError(detail, fallback = "Something went wrong.") {
    if (detail == null) return fallback;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) =>
                e && typeof e.msg === "string" ? e.msg : JSON.stringify(e),
            )
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}
